import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Empty, message, notification } from 'antd';
import { useMutation, useLazyQuery, useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';

import {
  getFilteredTemplates,
  filterTreeData,
  handleModalTreeSearchWrapper,
  addNewChildFromSearch,
} from 'helpers/contentlibrary';
import { GET_ALL_TEMPLATES_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import {
  UPDATE_CONTENT_LIBRARY,
  CONTENT_LIBRARY_TEMPLATE_DELETE,
  CONTENT_LIBRARY_ADD_CHILD,
} from 'graphql/mutations/contentLibraryMutations';
import { CREATE_PROPOSAL_MUTATION } from 'graphql/mutations/proposalMutation';
import { FETCH_PIPEDRIVE_DEALS_CLIENT_DETAILS } from 'graphql/queries/integrationQueries';
import { PROPOSAL_SUBSCRIPTION_TOPIC, TEMPLATES_LIMIT } from 'constants/index';
import { mondayBoardIdVar, templatesVar, hubspotUrlParams } from 'graphql/cache';
import { ContentLoader } from '../ContentLoader/ContentLoader';
import { useContentLibrary } from 'context/ContentLibraryContext';
import { sanitizeObject } from 'utils/xss';

import ConfirmModal from 'components/ConfirmModal';
import CreateDuplicateProposalModal from 'components/CreateDuplicateProposalModal';
import ShareWithModal from 'components/ShareWithModal';
import utils from 'utils/utils';
import { canUserLock, canUserUnlock } from 'helpers/proposal';

import TemplateCard from '../TemplateCard/TemplateCard';
import PreviewProposalModal from '../PreviewProposalModal/PreviewProposalModal';
import ContentLibraryTree from '../ContentLibraryTree/ContentLibraryTree';
import ContentLibraryModals from '../../ContentLibraryModals';

/**
 * Template tab
 *
 * @param {Object} props
 * @param {boolean} props.disabledFilter
 * @param {Array} props.filterItems
 * @param {string} props.searchValue
 * @param {Object} props.user
 * @param {Object} props.config
 * @param {Function} props.loadConfig
 * @param {Function} props.saveUser
 * @param {Array} props.teamMembers
 * @param {string} props.sortValue
 * @param {boolean} props.isLocked
 */
const TemplateTab = ({
  disabledFilter,
  filterItems,
  searchValue,
  user,
  config,
  loadConfig,
  saveUser,
  teamMembers,
  sortValue,
  isLocked,
}) => {
  // Get treeData
  const {
    treeData,
    selectedFolderKey,
    onContentLibraryKeySelect,
    setIndexes,
    initialTreeData,
    addKey,
    handleRefetch,
    onSelectTreeItem,
    treeSearch,
  } = useContentLibrary();

  /**
   * Filtered tree data
   */
  const filteredTreeData = filterTreeData(treeData, 'templates');

  const [createModal, showCreateModal] = useState(false);
  const [previewModal, showPreviewModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [confirmModal, showConfirmModal] = useState(false);
  const [shareModal, showShareModal] = useState(false);
  const [moveModal, showMoveModal] = useState(false);
  const [showDuplicateTemplate, showDuplicateTemplateModal] = useState(false);
  const [selectedTemplateEditValue, setSelectedTemplateEditValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [starredTemplates, setStarredTemplates] = useState([]);
  const [dealClientName, setDealClientName] = useState('');
  const [dealClientEmail, setDealClientEmail] = useState('');
  const [pipedriveDealId, setPipedriveDealId] = useState('');
  const [dontOverWriteHeader, setDontOverWriteHeader] = useState(false);
  const [modalTreeData, setModalTreeData] = useState({ children: [] });
  const [lockLoadingMap, setLockLoadingMap] = useState({});

  const templates = useReactiveVar(templatesVar || []);

  const contentLibraryTreeRef = useRef();
  const allTemplateCursorRef = useRef(null);
  const isLoadingMoreRef = useRef(null);
  const templateFetchedRef = useRef(false);
  const loaderRef = useRef();
  const perPageLimit =
    TEMPLATES_LIMIT * (window.innerHeight > 900 && window.innerWidth > 1800 ? 2 : 1);

  const processFetchedData = (data, shouldConcat = false) => {
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);

    if (!data?.fetchContentLibraryTemplatesItems) {
      return;
    }

    const { templates: newTemplates, pagingMetadata } = data.fetchContentLibraryTemplatesItems;

    if (shouldConcat) {
      const oldTemplates = templatesVar();

      // remove duplicates
      const existingIds = new Set(oldTemplates.map((item) => item._id));
      const filteredNewTemplates = [];

      for (const item of newTemplates) {
        if (!existingIds.has(item._id)) {
          existingIds.add(item._id);
          filteredNewTemplates.push(item);
        }
      }

      templatesVar([...oldTemplates, ...filteredNewTemplates]);

      if (filteredNewTemplates.length > 0) {
        allTemplateCursorRef.current = pagingMetadata;
      }
    } else {
      templatesVar(newTemplates);
      allTemplateCursorRef.current = pagingMetadata;
    }
  };

  /**
   * Fetch all templates
   */
  const [fetchAllTemplates, { refetch, fetchMore, loading }] = useLazyQuery(
    GET_ALL_TEMPLATES_LIBRARIES,
    {
      fetchPolicy: 'network-only',
      // fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        processFetchedData(data);
        templateFetchedRef.current = true;
      },
    }
  );

  // Update modal tree data when context tree data changes
  useEffect(() => {
    setModalTreeData(filteredTreeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  /**
   * Use effect
   */
  useEffect(() => {
    fetchAllTemplates();
  }, [fetchAllTemplates]);

  /**
   * Fetch pipedrive deal client details query
   */
  const [fetchPipedriveDealClientDetails] = useLazyQuery(FETCH_PIPEDRIVE_DEALS_CLIENT_DETAILS, {
    skip: !user._id,
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchPipedriveDealsClientDetails }) => {
      setDealClientName(fetchPipedriveDealsClientDetails.dealClientName);
      setDealClientEmail(fetchPipedriveDealsClientDetails.dealClientEmail);
      setPipedriveDealId(fetchPipedriveDealsClientDetails.dealId);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  /**
   * Use effect
   */
  useEffect(() => {
    const pipedriveSelectedId = utils.readCookie('pipedriveSelectedId');
    if (pipedriveSelectedId) {
      fetchPipedriveDealClientDetails({
        variables: {
          pipedriveSelectedId,
          pipedriveResource: utils.readCookie('pipedriveResource'),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPipedriveDealClientDetails]);

  /**
   * Refetch templates
   *
   * @param {Object} params
   * @param {string} params.key
   * @param {string} params.search
   * @param {Array} params.filter
   * @param {string} params.sort
   */
  const refetchTemplates = useCallback(
    async ({ key, search, filter, sort }) => {
      if (user?.paymentStatus?.canLock) {
        return;
      }
      const { data } = await refetch({
        limit: perPageLimit,
        search,
        filterItem: filter?.find((filterItem) => filterItem.checked)?.key,
        key,
        sort,
      });

      processFetchedData(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Fetch more templates
   *
   */
  const fetchMoreTemplates = useCallback(async () => {
    const allTemplateCursor = allTemplateCursorRef.current;

    if (!allTemplateCursor?.hasNext || isLoadingMoreRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    const { data } = await fetchMore({
      variables: {
        limit: perPageLimit,
        cursorPaging: allTemplateCursor.cursor,
        search: searchValue,
        filterItem: filterItems?.find((x) => x.checked)?.key,
        sort: sortValue,
      },
    });

    processFetchedData(data, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, filterItems, sortValue]);

  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (!target?.isIntersecting) return;
      fetchMoreTemplates();
    },
    [fetchMoreTemplates]
  );

  const container = document.querySelector('.template-card-container');

  useEffect(() => {
    const option = { root: container, rootMargin: '20px', threshold: 0 };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleObserver]);

  /**
   * Update content item
   */
  const [updateContentItem] = useMutation(UPDATE_CONTENT_LIBRARY, {
    onCompleted() {
      refetchTemplates({
        key: selectedFolderKey,
        search: searchValue,
        filter: filterItems,
        sort: sortValue,
      });
    },
  });

  /**
   * Create proposal
   */
  const [createProposal] = useMutation(CREATE_PROPOSAL_MUTATION, {
    onCompleted: ({ createProposal }) => {
      showCreateModal(false);
      setIsLoading(false);
      if (createProposal) {
        utils.deleteCookie('pipedriveToken');
        utils.deleteCookie('pipedriveResource');
        utils.deleteCookie('pipedriveSelectedId');
        history.push(`/d/${createProposal.pid}`);
      } else {
        message.error(`Problem creating proposal`);
      }
    },
    onError: (error) => {
      setIsLoading(false);
      console.log(error, 'error duplicating');
      message.error(`Problem creating proposal ${error.message}`);
    },
  });

  /**
   * Delete template
   */
  const [deleteTemplate] = useMutation(CONTENT_LIBRARY_TEMPLATE_DELETE, {
    onCompleted() {
      templatesVar(templates.filter((template) => template._id !== selectedTemplateId));
      message.success('Template deleted successfully');
      showConfirmModal(false);
      setSelectedTemplateId('');
    },
    onError() {
      message.error('Template deletion failed');
      showConfirmModal(false);
    },
  });

  /**
   * Add child mutation
   */
  const [addChild] = useMutation(CONTENT_LIBRARY_ADD_CHILD, {
    onCompleted() {
      notification.success({
        description: 'Created new folder successfully.',
      });
      handleRefetch(treeSearch, onSelectTreeItem);
    },
    onError() {},
  });

  /**
   * Use effect
   */
  useEffect(() => {
    if (!templateFetchedRef?.current) {
      return;
    }

    refetchTemplates({
      key: selectedFolderKey,
      search: searchValue,
      filter: filterItems,
      sort: sortValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, filterItems, sortValue, selectedFolderKey, user.deletedTemplates]);

  /**
   * History
   */
  const history = useHistory();

  /**
   * Filtered templates
   */
  const filteredTemplates = getFilteredTemplates({ templates });

  const { starred: starredItems, all: allItems } = filteredTemplates;

  /**
   * Hide or enable template from user
   *
   * @param {Object} params
   * @param {string} params.templateId
   */
  const hideOrEnableTemplateFromUser = ({ templateId }) => {
    let deletedTemplates = [...user.deletedTemplates] || [];

    if (!deletedTemplates.includes(templateId)) {
      deletedTemplates.push(templateId);
    } else {
      deletedTemplates = deletedTemplates.filter((e) => e !== templateId);
    }

    saveUser({
      variables: {
        user: {
          id: user._id,
          deletedTemplates,
        },
      },
    });
  };

  /**
   * Star or unstar template
   *
   * @param {Object} params
   * @param {boolean} params.starred
   * @param {string} params.id
   */
  const starOrUnstar = ({ starred, id }) => {
    updateContentItem({
      variables: {
        id: id,
        type: 'templates',
        item: { starred },
      },
    });
  };

  /**
   * Confirm delete template
   */
  const confirmDeleteTemplate = () => {
    deleteTemplate({
      variables: {
        _id: selectedTemplateId,
        folderKey: selectedFolderKey,
      },
    });
  };

  /**
   * Create proposal from template
   *
   * @param {Object} params
   * @param {Object} params.values
   * @param {Object} params.proposal
   */
  const createProposalFromTemplate = ({ values, proposal }) => {
    setIsLoading(true);
    const { _id } = proposal;

    values = sanitizeObject(values);

    const {
      client_name: newClientName,
      project_name: newProjectName,
      client_email: newClientMail,
      client_company: newClientCompany,
      monday_item_id: mondayItemId,
      pipedrive_deal_id: pipedriveDealId,
      hubspot_contact_id: hubspotContactId,
    } = values;

    const additionalInfo = {
      monday: {
        boardId: mondayBoardIdVar(),
        itemId: mondayItemId,
      },
      dontOverWriteHeader,
      pipedrive: {
        pipedriveDealId,
      },
      hubspot: {
        hubspotObjectType: hubspotUrlParams().from || 'contact',
        hubspotContactId: hubspotContactId,
        hubspotDealId: hubspotUrlParams().hubspotDealId || '',
        hubspotCompanyId: hubspotUrlParams().hubspotCompanyId || '',
      },
    };

    createProposal({
      variables: {
        pid: _id,
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`,
        template: {
          newProjectName: newProjectName,
          newClientName: newClientName,
          newClientMail: newClientMail || '',
          newClientExtra: {
            company: newClientCompany || '',
          },
        },
        additionalInfo,
      },
    });
  };

  /**
   * On click menu item
   *
   * @param {Object} params
   * @param {string} params.actionType
   * @param {string} params.templateId
   * @param {boolean} params.starred
   * @param {Object} params.templateData
   * @param {string} params.templateName
   */
  const onClickMenuItem = async ({
    actionType,
    templateId,
    starred,
    templateData,
    templateName,
  }) => {
    if (actionType === 'delete') {
      setSelectedTemplateId(templateId);
      showConfirmModal(true);
    } else if (actionType === 'hideorenable') {
      hideOrEnableTemplateFromUser({ templateId });
    } else if (actionType === 'starOrUnstar') {
      starOrUnstar({ starred, id: templateId });
    } else if (actionType === 'use_template') {
      const template = templates.find((item) => item._id === templateId);
      setSelectedTemplate(template);
      showCreateModal(true);
    } else if (actionType === 'use_preview_as_template') {
      const template = templates.find((item) => item._id === templateId);
      showPreviewModal(false);
      setSelectedTemplate(template);
      showCreateModal(true);
    } else if (actionType === 'preview') {
      setSelectedTemplate(templateData);
      showPreviewModal(true);
    } else if (actionType === 'edit_template') {
      history.push(`/t/${templateId}`);
    } else if (actionType === 'rename') {
      setSelectedTemplateId(templateId);
      setSelectedTemplateEditValue(templateName);
    } else if (actionType === 'lock_unlock') {
      const template = templates.find((item) => item._id === templateId);
      setLockLoadingMap((prev) => ({ ...prev, [template._id]: true }));
      const { lockedData } = template;

      if (!lockedData && !canUserLock(user)) {
        message.error('You do not have permission to lock this template.');
        setLockLoadingMap((prev) => ({ ...prev, [template._id]: false }));
        return;
      }

      if (lockedData && !canUserUnlock(user, lockedData)) {
        message.error(
          'You do not have permission to unlock this template. Only the user who locked it or acccount admin can unlock it.'
        );
        setLockLoadingMap((prev) => ({ ...prev, [template._id]: false }));
        return;
      }

      let templateUpdate = {
        lockedData: null,
      };

      if (!lockedData) {
        templateUpdate = {
          lockedData: {
            uid: user._id,
            name: user?.profile?.name,
            value: true,
            date: new Date().toISOString(),
          },
        };
      }

      await updateContentItem({
        variables: {
          id: templateId,
          type: 'templates',
          item: templateUpdate,
        },
        onCompleted: async () => {
          await refetchTemplates({
            key: selectedFolderKey,
            search: searchValue,
            filter: filterItems,
            sort: sortValue,
          });
          setLockLoadingMap((prev) => ({ ...prev, [template._id]: false }));
          if (templateUpdate?.lockedData) {
            utils.showLockedNotification(
              template.pid,
              'Only you or your account admin can unlock it.'
            );
          }
        },
        onError: () => {
          setLockLoadingMap((prev) => ({ ...prev, [template._id]: false }));
        },
      });
    } else if (actionType === 'save') {
      await updateContentItem({
        variables: {
          id: templateId,
          type: 'templates',
          item: { templateName: selectedTemplateEditValue },
        },
      });

      setSelectedTemplateId('');
      setSelectedTemplateEditValue('');
    } else if (actionType === 'share') {
      const template = templates.find((item) => item._id === templateId);
      setSelectedTemplate(template);
      showShareModal(true);
    } else if (actionType === 'move') {
      const template = templates.find((item) => item._id === templateId);
      setSelectedTemplate(template);
      showMoveModal(true);
    } else if (actionType === 'duplicate') {
      const template = templates.find((item) => item._id === templateId);
      setSelectedTemplate(template);
      showDuplicateTemplateModal(true);
    }
  };

  /**
   * On key select
   *
   * @param {Array} selectedFolderKey
   */
  const onKeySelect = (selectedFolderKey, isMoveModal) => {
    if (!isMoveModal) {
      onContentLibraryKeySelect(selectedFolderKey);
      templatesVar([]);
      setStarredTemplates([]);
    }
  };

  /**
   * Check if templates are empty
   */
  const isTemplatesEmpty =
    starredTemplates === 'onlyStarred'
      ? !starredItems.length
      : !(starredItems.length || allItems.length);

  /**
   * Render handle tree search
   *
   * @param {string} tempSearchValue
   */
  const renderHandleTreeSearch = (tempSearchValue) =>
    handleModalTreeSearchWrapper({
      tempSearchValue,
      treeData: filteredTreeData,
      setIndexes,
      initialTreeData,
      addKey,
      setModalTreeData,
      filteredTreeData,
    });

  /**
   * Render add new child from search
   *
   * @param {string} treeSearchValue
   */
  const renderAddNewChildFromSearch = (treeSearchValue) =>
    addNewChildFromSearch({
      treeSearchValue,
      selectedFolderKey,
      user,
      treeData,
      addChild,
    });

  return (
    <div className="content-library-item">
      <ContentLibraryTree
        ref={contentLibraryTreeRef}
        type="templates"
        onUpdate={() =>
          refetchTemplates({
            key: selectedFolderKey,
            search: searchValue,
            filter: filterItems,
            sort: sortValue,
          })
        }
        userId={user && user._id}
        userRole={user?.roles && user?.roles[0]}
        isLocked={isLocked}
        treeData={treeData}
        onKeySelect={onKeySelect}
      />

      <div className="split-d2 template-card-container">
        {loading ? (
          <ContentLoader />
        ) : (
          <>
            {starredItems.length ? (
              <div className="stared-header">
                MY STARRED TEMPLATES
                <Badge className="star-badge" count={starredItems.length} />
              </div>
            ) : null}
            <React.Fragment>
              {isTemplatesEmpty && (
                <div className="item-row empty-wrapper">
                  <Empty
                    className="empty-view"
                    description="Looks like you have no template files yet"
                  />
                </div>
              )}

              {starredItems?.length > 0 && (
                <div className="item-row template-cards">
                  {starredItems.map((item) => {
                    return (
                      <TemplateCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        templateUserId={item.uid}
                        templateAuid={item.auid}
                        templateId={item._id}
                        title={item.templateName ? item.templateName : item.project.name}
                        thumbnail={item?.templateUrl}
                        key={item.pid}
                        description={item.description}
                        starOrUnstar={starOrUnstar}
                        isStarred
                        disabledFilter={disabledFilter}
                        onClickMenuItem={onClickMenuItem}
                        config={config}
                        user={user}
                        itemEditable={selectedTemplateId === item._id}
                        setEditValue={setSelectedTemplateEditValue}
                        isLocked={isLocked}
                        variables={item?.draft?.variables}
                        lockedData={item?.lockedData}
                        lockLoadingMap={lockLoadingMap}
                      />
                    );
                  })}
                </div>
              )}

              {starredTemplates !== 'onlyStarred' && starredItems.length && allItems.length ? (
                <div className="divider-line" />
              ) : null}

              <div className="item-row template-cards">
                {starredTemplates !== 'onlyStarred' &&
                  allItems.map((item) => {
                    return (
                      <TemplateCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        title={item.templateName ? item.templateName : item?.project?.name}
                        thumbnail={item?.templateUrl}
                        key={item._id}
                        description={item.description}
                        starOrUnstar={starOrUnstar}
                        templateId={item._id}
                        templateUserId={item.uid}
                        templateAuid={item.auid}
                        disabledFilter={disabledFilter}
                        onClickMenuItem={onClickMenuItem}
                        config={config}
                        user={user}
                        itemEditable={selectedTemplateId === item._id}
                        setEditValue={setSelectedTemplateEditValue}
                        isLocked={isLocked}
                        variables={item?.draft?.variables}
                        lockedData={item?.lockedData}
                        lockLoadingMap={lockLoadingMap}
                      />
                    );
                  })}
              </div>

              {isLoadingMore && <ContentLoader length={4} />}
            </React.Fragment>
          </>
        )}

        <div ref={loaderRef} style={{ height: '30px' }} />
      </div>
      {createModal && (
        <CreateDuplicateProposalModal
          modalType="Create"
          user={user}
          visible={createModal}
          isLoading={isLoading}
          onCancel={() => showCreateModal(false)}
          selectedProposal={selectedTemplate}
          onConfirm={createProposalFromTemplate}
          dealId={pipedriveDealId}
          dealClientName={dealClientName}
          dealClientEmail={dealClientEmail}
          isFromTemplate={true}
          dontOverWriteHeader={dontOverWriteHeader}
          setDontOverWriteHeader={setDontOverWriteHeader}
        />
      )}
      {previewModal && (
        <PreviewProposalModal
          onClickMenuItem={onClickMenuItem}
          onCancel={() => showPreviewModal(false)}
          template={selectedTemplate}
          config={config}
          loadConfig={loadConfig}
          user={user}
          isLocked={isLocked}
          title={
            selectedTemplate.selectedTemplateName ||
            selectedTemplate.titletext ||
            (selectedTemplate.draft &&
              selectedTemplate.draft.header &&
              selectedTemplate.draft.header.title) ||
            (selectedTemplate.project && selectedTemplate.project.name)
          }
        />
      )}
      {shareModal && (
        <ShareWithModal
          onCancel={() => showShareModal(false)}
          template={selectedTemplate}
          teamMembers={teamMembers}
          visible={shareModal}
          isLocked={isLocked}
        />
      )}
      {confirmModal && (
        <ConfirmModal
          title="Delete Template?"
          body="This action cannot be undone"
          visible={confirmModal}
          cancelText="CANCEL"
          confirmText="YES, DELETE"
          onCancel={() => showConfirmModal(false)}
          onConfirm={confirmDeleteTemplate}
        />
      )}
      <ContentLibraryModals
        language={selectedTemplate?.language}
        visible={moveModal || showDuplicateTemplate}
        onCancel={
          showDuplicateTemplate
            ? () => showDuplicateTemplateModal(false)
            : () => showMoveModal(false)
        }
        isDashboard={showDuplicateTemplate ? true : false}
        isTemplate={showDuplicateTemplate ? true : false}
        template={selectedTemplate}
        selectedFolderKey={selectedFolderKey}
        treeData={modalTreeData}
        onKeySelect={onKeySelect}
        handleTreeSearch={renderHandleTreeSearch}
        addNewChildFromSearch={renderAddNewChildFromSearch}
        refetchTemplates={() =>
          refetchTemplates({
            key: selectedFolderKey,
            search: searchValue,
            filter: filterItems,
            sort: sortValue,
          })
        }
      />
    </div>
  );
};

/**
 * Default props
 */
TemplateTab.defaultProps = {
  searchValue: '',
  loading: false,
  disabledFilter: false,
  templates: [],
  deletedTemplates: [],
  config: {},
  isLocked: false,
};

/**
 * Prop types
 */
TemplateTab.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.shape({})),
  deletedTemplates: PropTypes.arrayOf(PropTypes.string),
  filterItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  searchValue: PropTypes.string,
  loading: PropTypes.bool,
  disabledFilter: PropTypes.bool,
  user: PropTypes.instanceOf(Object).isRequired,
  config: PropTypes.instanceOf(Object),
  saveUser: PropTypes.func.isRequired,
  sortValue: PropTypes.string.isRequired,
  teamMembers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLocked: PropTypes.bool,
};

export default TemplateTab;
